import { useSignedMedia } from "@/lib/hooks/use-signed-media";
import {
  EMPTY_STORY_GRADIENT,
  STORY_DESIGN,
  STORY_LAYOUT,
  STORY_META,
  STORY_STAGE,
  storyColor,
  type StoryDecal,
  type StoryFrame,
  type StoryKey,
  type StorySection,
} from "@/lib/mentor-stories/story-content";
import { cn } from "@/lib/utils";
import { memo } from "react";

function FramePhoto({
  frame,
  url,
  label,
}: {
  frame: StoryFrame;
  url?: string;
  label: string;
}) {
  const signed = useSignedMedia(url);
  const border = frame.border ?? 2;
  const hole = frame.hole;
  const holeMask = hole
    ? `radial-gradient(circle at center, transparent ${(hole - border * 2) / 2}px, black ${(hole - border * 2) / 2}px)`
    : undefined;

  return (
    <div
      className="absolute flex items-center justify-center overflow-hidden border-solid border-brand-trueWhite bg-brand-trueWhite/70 text-center font-body text-body-4 font-bold text-text-primary"
      style={{
        borderRadius: frame.radius ?? 0,
        borderWidth: hole ? 0 : border,
        boxShadow: hole ? `inset 0 0 0 ${border}px #fff` : undefined,
        height: frame.h,
        left: frame.x,
        maskImage: holeMask,
        top: frame.y,
        transform: frame.rotate ? `rotate(${frame.rotate}deg)` : undefined,
        WebkitMaskImage: holeMask,
        width: frame.w,
      }}
    >
      {signed.url ? (
        <img src={signed.url} alt="" className="size-full object-cover" />
      ) : (
        <span className="px-sm">{label}</span>
      )}
      {hole ? (
        <span
          className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border-solid border-brand-trueWhite"
          style={{ borderWidth: border, height: hole, width: hole }}
        />
      ) : null}
    </div>
  );
}

function Decal({ decal }: { decal: StoryDecal }) {
  return (
    <img
      src={decal.src}
      alt=""
      className="pointer-events-none absolute"
      style={{ height: decal.h, left: decal.x, top: decal.y, width: decal.w }}
    />
  );
}

export const StoryPreview = memo(function StoryPreview({
  storyKey,
  section,
  className,
}: {
  storyKey: StoryKey;
  section: StorySection;
  className?: string;
}) {
  const color = storyColor(section.color) ?? EMPTY_STORY_GRADIENT;
  const meta = STORY_META[storyKey];
  const layout = STORY_LAYOUT[storyKey];
  const caption = section.caption?.trim();
  const offset = layout.offsetY;

  const fit = STORY_STAGE.width / STORY_DESIGN.width;

  return (
    <div
      className={cn("relative overflow-hidden rounded-[19px]", className)}
      style={{
        background: `linear-gradient(180deg, ${color.from} 0%, ${color.from} 22%, ${color.to} 100%)`,
        height: STORY_STAGE.height,
        width: STORY_STAGE.width,
      }}
    >
      <div
        className="absolute left-0 top-0 origin-top-left"
        style={{
          height: STORY_DESIGN.height,
          transform: `scale(${fit})`,
          width: STORY_DESIGN.width,
        }}
      >
        {layout.behind.map((decal) => (
          <Decal key={decal.src} decal={{ ...decal, y: decal.y + offset }} />
        ))}
        <div
          className={cn(
            "absolute inset-x-[22px] flex flex-col gap-[5px] text-text-primary",
            layout.align === "start" ? "items-start text-left" : "items-center text-center",
          )}
          style={{ top: 86 + offset }}
        >
          <p className="font-mono text-xs">{meta.eyebrow}</p>
          {caption ? (
            <p
              className="font-heading font-bold"
              style={{
                fontSize: layout.captionSize,
                lineHeight: `${layout.captionSize}px`,
              }}
            >
              {caption.toLowerCase()}
            </p>
          ) : null}
        </div>
        {layout.frames.map((frame) => (
          <FramePhoto
            key={frame.slot}
            frame={{ ...frame, y: frame.y + offset }}
            label={meta.slots.find((slot) => slot.slot === frame.slot)?.label ?? ""}
            url={section.photos.find((photo) => photo.slot === frame.slot)?.url}
          />
        ))}
        {layout.front.map((decal) => (
          <Decal key={decal.src} decal={{ ...decal, y: decal.y + offset }} />
        ))}
      </div>
    </div>
  );
});
